import { reactive, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { object, string, array } from 'yup'
import { useResultReject } from './useResultReject'
import { usePoliticalActions } from './usePoliticalActions'
import { usePoliticalActionDialog } from './usePoliticalActionDialog'
import { useNotifications } from '@/services/notification/useNotifications'
import { useDialog } from '@/services/useDialog'
import { useLogs } from '@/services/useLogs'
import { useTopData } from '@/services/useHome'
import { useLoader } from '../useLoader'
import { useError } from '../useError'
import axios from '@/axios'

const tabs = reactive([
  {
    name: '概要',
    required: true,
    status: 0,
    fields: ['overview'],
  },
  { name: '備考', required: true, status: 0, fields: ['remarks'] },
  { name: '画像添付', required: true, status: 0 },
  { name: '資料添付', required: true, status: 0 },
  { name: '事務局より', required: false, status: 0 },
])

export const useResultFormTabs = () => {
  return { tabs }
}

const schema = object({
  overview: string()
    .required('必須です')
    .typeError('必須です'),
  remarks: string().nullable(),
  files: array(),
  additionalResources: array(),
})

export const useResultForm = () => {
  const {
    currentPoliticalAction,
    getActivitiesAfterResponse,
  } = usePoliticalActions()
  const { activityDialog } = usePoliticalActionDialog()
  const { getNotificationsAfterResponse } = useNotifications()
  const { getTimelineAfterResponse } = useLogs()
  const { getTopDataAfterResponse } = useTopData()
  const { toggleShowLoader } = useLoader()
  const { toggleShowError } = useError()
  const route = useRoute()

  watch(activityDialog, () => {
    if (activityDialog.isPreviewResult) {
      currentPoliticalAction.report.overview = overview.value
      currentPoliticalAction.report.remarks = remarks.value
    }
  })
  onMounted(() => {
    for (const x of Array(3).keys()) {
      setStatus(x)
    }
  })
  const { meta, handleSubmit } = useForm({ validationSchema: schema })

  watch(meta, meta => {
    meta.valid
      ? (activityDialog.isPreviewResultEnabled = true)
      : (activityDialog.isPreviewResultEnabled = false)
  })

  const { value: overview, errorMessage: overviewError } = useField(
    'overview',
    undefined,
    {
      initialValue: currentPoliticalAction.report.overview,
    },
  )

  const { value: remarks, errorMessage: remarksError } = useField(
    'remarks',
    undefined,
    { initialValue: currentPoliticalAction.report.remarks },
  )

  const { value: files, errorMessage: filesError } = useField(
    'files',
    undefined,
    { initialValue: currentPoliticalAction.report.images },
  )
  const {
    value: additionalResources,
    errorMessage: additionalResourcesError,
  } = useField('additionalResources', undefined, {
    initialValue: currentPoliticalAction.report.additionalResources,
  })

  const handleImages = files => {
    if (files.length > 0) {
      tabs[2].status = 1
    } else {
      tabs[2].status = 0
    }
  }

  const handleFiles = files => {
    if (files.length > 0) {
      tabs[3].status = 1
    } else {
      tabs[3].status = 0
    }
  }

  const getSentBackItemMessage = attr => {
    const sentBackList = []
    if (
      currentPoliticalAction.report.option.viewType === 0 ||
      currentPoliticalAction.report.option.viewType === 1 ||
      currentPoliticalAction.report.sentBackItems.length == 0
    ) {
      return []
    }
    currentPoliticalAction.report.sentBackItems.forEach(item => {
      if (!item || item.length == 0) return []
      const backItem = item.items.find(item => item.target == attr)
      if (backItem !== undefined) {
        sentBackList.push(backItem)
      }
    })
    if (sentBackList.length > 0) {
      sentBackList
      return sentBackList
    }
    return []
  }

  // 政務活動ダイアログ「入力を確認する」ボタンを押したときにバリデーションを更新する。エラーがなければプレビュー画面へ
  const resultSubmit = handleSubmit(() => {
    activityDialog.isPreviewResult = true
  })

  watch(overview, () => {
    currentPoliticalAction.report.overview = overview.value
    setStatus(0)
  })
  watch(remarks, () => {
    currentPoliticalAction.report.remarks = remarks.value
    setStatus(1)
  })

  // validateのタイミングでタブのstatusを更新する
  watch(overviewError, () => {
    setStatus(0)
  })

  const setStatus = index => {
    const err = error(index)
    const inp = input(index)
    if (err && inp) {
      tabs[index].status = 1
    } else if (err && !inp) {
      tabs[index].status = 0
    } else if (!err) {
      tabs[index].status = -1
    }
  }

  // エラーがある時falseを返す
  const error = index => {
    let err = false

    if (index === 0) {
      err = typeof overviewError.value === 'undefined'
    } else {
      err = true
    }
    return err
  }

  const input = index => {
    let inp = false
    if (index === 0) {
      inp = overview.value
    }
    if (index === 1) {
      inp = remarks.value
    }
    if (index === 2) {
      inp = currentPoliticalAction.report.images.length > 0
    }
    if (index === 3) {
      inp = currentPoliticalAction.report.additionalResources.length > 0
    }
    return inp
  }

  const submitActivity = async (id, type) => {
    try {
      toggleShowLoader(true)
      // validate
      if (!(typeof id === 'number' && id > 0)) {
        throw new Error('invalid id: ' + id)
      }
      if (!(type === 'report' || type === 'report-apply')) {
        throw new Error('invalid type: ' + type)
      }
      // fileの送信を行うためformDataオブジェクトを使用する
      const formdata = new FormData()

      // 保存タイプのフラグのフォームデータを作成
      formdata.append('is_temporary', type === 'report')
      formdata.append(
        'document_type',
        currentPoliticalAction.report.documentType,
      )

      // 概要ののフォームデータを作成
      if (currentPoliticalAction.report.overview) {
        formdata.append(
          'overview',
          currentPoliticalAction.report.overview === null
            ? ''
            : currentPoliticalAction.report.overview,
        )
      }

      // 備考のフォームデータを作成
      if (currentPoliticalAction.report.remarks) {
        formdata.append(
          'remarks',
          currentPoliticalAction.report.remarks === null
            ? ''
            : currentPoliticalAction.report.remarks,
        )
      }

      // 添付資料のフォームデータを作成
      currentPoliticalAction.report.additionalResources.forEach(file => {
        if (file?.display) {
          formdata.append('keep_file_names[]', file.display)
        } else {
          formdata.append('additional_resources[]', file)
        }
      })

      // 画像のフォームデータを作成
      for (let i = 0; i < currentPoliticalAction.report.images.length; i++) {
        Object.keys(currentPoliticalAction.report.images[i]).forEach(key => {
          if (currentPoliticalAction.report.images[i][key]) {
            const keyName = 'images[' + i + '][' + key + ']'
            formdata.append(
              keyName,
              currentPoliticalAction.report.images[i][key],
            )
          }
        })
      }
      // postだとだとマルチパート形式のファイル送信がうまくいかないためputをオーバーライド
      let config = {
        headers: {
          'content-type': 'multipart/form-data',
          'X-HTTP-Method-Override': 'PUT',
        },
      }

      const res = await axios.post(`/actions/${id}`, formdata, config)
      if (route.name === 'Activities') {
        await getActivitiesAfterResponse(res)
      } else if (route.name !== 'Activities') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(res)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(res)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(res)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const cancelRequest = async (id, type) => {
    try {
      toggleShowLoader(true)
      const res = await axios.post(`/actions/cancel`, {
        document_id: id,
        document_type: type,
      })
      if (route.name === 'Activities') {
        await getActivitiesAfterResponse(res)
      } else if (route.name !== 'Activities') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(res)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(res)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(res)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const approveRequest = async (id, documentId, type, userRank) => {
    try {
      toggleShowLoader(true)
      const { rejectedAttrs, toSnake } = useResultReject()
      let data = {}
      // 会派事務局の時にアップデートを行う
      if (userRank === 2) {
        // fileの送信を行うためformDataオブジェクトを使用する
        const formdata = new FormData()

        // 保存タイプのフラグのフォームデータを作成
        formdata.append('is_temporary', false)
        formdata.append(
          'document_type',
          currentPoliticalAction.report.documentType,
        )

        // 概要ののフォームデータを作成
        if (currentPoliticalAction.report.overview) {
          formdata.append('overview', currentPoliticalAction.report.overview)
        }

        // 備考のフォームデータを作成
        if (currentPoliticalAction.report.remarks) {
          formdata.append('remarks', currentPoliticalAction.report.remarks)
        }

        // 添付資料のフォームデータを作成
        currentPoliticalAction.report.additionalResources.forEach(file => {
          if (file?.display) {
            formdata.append('keep_file_names[]', file.display)
          } else {
            formdata.append('additional_resources[]', file)
          }
        })

        // 画像のフォームデータを作成
        for (let i = 0; i < currentPoliticalAction.report.images.length; i++) {
          Object.keys(currentPoliticalAction.report.images[i]).forEach(key => {
            if (currentPoliticalAction.report.images[i][key]) {
              const keyName = 'images[' + i + '][' + key + ']'
              formdata.append(
                keyName,
                currentPoliticalAction.report.images[i][key],
              )
            }
          })
        }
        // postだとだとマルチパート形式のファイル送信がうまくいかないためputをオーバーライド
        let config = {
          headers: {
            'content-type': 'multipart/form-data',
            'X-HTTP-Method-Override': 'PUT',
          },
        }
        await axios.post(`/actions/${id}`, formdata, config)
      }

      if (rejectedAttrs.length !== 0) {
        toSnake()
        data = {
          document_id: documentId,
          document_type: type,
          sent_back_items: rejectedAttrs,
        }
      } else {
        data = {
          document_id: documentId,
          document_type: type,
        }
      }

      const res = await axios.post(`/actions/approve`, data)
      if (route.name === 'Activities') {
        await getActivitiesAfterResponse(res)
      } else if (route.name !== 'Activities') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(res)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(res)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(res)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  return {
    meta,
    submitActivity,
    overview,
    overviewError,
    remarks,
    remarksError,
    files,
    filesError,
    additionalResources,
    additionalResourcesError,
    handleSubmit,
    resultSubmit,
    cancelRequest,
    approveRequest,
    getSentBackItemMessage,
    handleImages,
    handleFiles,
  }
}
